// src/tabs/EventsTab.jsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { currencyFmt } from '@/utils/ui';
import EventPurchaseDrawer from './EventPurchaseDrawer';
import { useTranslation } from 'react-i18next';

const EventsTab = ({ events = [], groupCurrency = 'USD' }) => {
  const { t } = useTranslation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);

  const openDrawer = (ev) => {
    setActiveEvent(ev);
    setDrawerOpen(true);
  };

  if (!events.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Calendar
            className="mx-auto mb-4 text-slate-400"
            size={48}
          />
          <h3 className="text-lg font-semibold mb-2">
            {t('groupSpace.events.noEventsTitle')}
          </h3>
          <p className="text-slate-600">
            {t('groupSpace.events.noEventsDescription')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((ev) => (
          <Card
            key={ev.id}
            data-testid={`event-card-${ev.id}`}
          >
            <CardHeader>
              <CardTitle className="text-lg">
                {ev.title}
              </CardTitle>
              <p className="text-sm text-slate-600">
                {ev.event_date
                  ? format(
                      new Date(ev.event_date),
                      'PPP p'
                    )
                  : t('groupSpace.events.dateTBA')}
              </p>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                {ev.description}
              </p>
              <p className="text-sm text-slate-700 mb-3">
                <strong>
                  {t('groupSpace.events.locationLabel')}
                </strong>{' '}
                {ev.location ||
                  t('groupSpace.events.locationTBA')}
              </p>
              {ev.capacity && (
                <p className="text-sm text-slate-600 mb-3">
                  {t('groupSpace.events.attendeesLabel', {
                    current: ev.attendee_count || 0,
                    capacity: ev.capacity,
                  })}
                </p>
              )}
              <div className="flex justify-between items-center">
                <span className="font-semibold">
                  {Number(ev.price || 0) > 0
                    ? currencyFmt(
                        ev.price,
                        ev.currency ||
                          groupCurrency ||
                          'CAD'
                      )
                    : t('groupSpace.events.free')}
                </span>
                <Button
                  size="sm"
                  onClick={() => openDrawer(ev)}
                  data-testid={`register-event-btn-${ev.id}`}
                >
                  {Number(ev.price || 0) > 0
                    ? t('groupSpace.events.buyButton')
                    : t('groupSpace.events.register')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <EventPurchaseDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setActiveEvent(null);
        }}
        eventObj={activeEvent}
        groupCurrency={groupCurrency}
      />
    </>
  );
};

export default EventsTab;
